import { useCallback, useEffect, useRef, useState } from 'react';

import { sleep } from '@karehero/helpers/timers';
import type { LiveTranscript } from '@karehero/models';

import { heardText, usableTranscript } from 'utils/voiceTranscript';
import { useFeatureFlag } from './useFeatureFlags';
import { useLiveTranscript } from './useLiveTranscript';

/** Whether recordings are transcribed as they happen. */
export const LIVE_TRANSCRIBE_FLAG = 'liveVoiceTranscribe';
/** Whether the words can be shown at all; they start hidden. */
export const LIVE_CAPTIONS_FLAG = 'liveVoiceCaptionsVisible';

/** How long after a recording stops its last words take to settle. */
export const TRANSCRIPT_SETTLE_MS = 1500;
/** How long a note sent as it stops waits for its session to end. */
export const TRANSCRIPT_END_WAIT_MS = 1000;

export interface UseVoiceTranscriptOptions {
  careCircleId?: string;
  /** The microphone while recording; null once it stops. */
  stream: MediaStream | null;
}

export interface UseVoiceTranscriptResult {
  /** The words as they are made, while transcribing is on at all. */
  caption?: LiveTranscript;
  isShown: boolean;
  /** Shows or hides the words; absent while they cannot be shown. */
  toggleShown?: () => void;
  /** A fault with the words shown; one with words not shown stays quiet. */
  error: string | null;
  /**
   * The transcript once its last words settle, or null to read the audio
   * instead. Past the first sentChars, already sent as heard, the rest is too.
   */
  takeTranscript: (
    durationSeconds: number,
    sentChars?: number,
  ) => Promise<string | null>;
  /** Where the API keeps the whole recording, or null to upload it instead. */
  recordingId: string | null;
}

/**
 * useVoiceTranscript transcribes a recording as it happens, so a voice note is
 * read from its words as soon as it is sent. The words stay hidden unless the
 * captions flag lets them be shown and they are toggled on.
 */
export const useVoiceTranscript = ({
  careCircleId,
  stream,
}: UseVoiceTranscriptOptions): UseVoiceTranscriptResult => {
  const isOn = !!useFeatureFlag(LIVE_TRANSCRIBE_FLAG);
  const isCaptionsFlagOn = !!useFeatureFlag(LIVE_CAPTIONS_FLAG);
  const canShow = isOn && isCaptionsFlagOn;
  const [isToggledOn, setIsToggledOn] = useState(false);
  const isShown = canShow && isToggledOn;
  const toggleShown = useCallback(() => setIsToggledOn((s) => !s), []);
  const live = useLiveTranscript({ isEnabled: isOn, careCircleId, stream });

  const latest = useRef(live);
  latest.current = live;
  const endedAt = useRef<number | null>(null);
  const endWaiters = useRef<(() => void)[]>([]);
  useEffect(() => {
    endedAt.current = live.status === 'ended' ? Date.now() : null;
    if (endedAt.current !== null) {
      endWaiters.current.splice(0).forEach((resolve) => resolve());
    }
  }, [live.status]);

  // The session ends a render after the recording stops, so a note sent as it
  // stops waits for it; one that never started has nothing to wait for.
  // Gives when it ended, or null if it doesn't.
  const untilEnded = useCallback(
    () =>
      new Promise<number | null>((resolve) => {
        if (endedAt.current !== null) return resolve(endedAt.current);
        if (latest.current.status === 'idle') return resolve(null);
        const timer = setTimeout(() => resolve(null), TRANSCRIPT_END_WAIT_MS);
        endWaiters.current.push(() => {
          clearTimeout(timer);
          resolve(endedAt.current);
        });
      }),
    [],
  );

  const takeTranscript = useCallback(
    async (durationSeconds: number, sentChars = 0) => {
      const ended = isOn ? await untilEnded() : null;
      if (ended === null) return null;
      const asStopped = latest.current;
      const settling = ended + TRANSCRIPT_SETTLE_MS - Date.now();
      if (settling > 0) await sleep(settling);
      // A recording started meanwhile has words of its own; keep this one's.
      const heard =
        latest.current.status === 'ended' ? latest.current : asStopped;
      // Its words up to a pause went as heard, so the last few go the same way:
      // the audio is kept with them, not read again.
      if (sentChars > 0) return heard.error ? null : heardText(heard);
      return usableTranscript(heard, !!heard.error, durationSeconds);
    },
    [isOn, untilEnded],
  );

  return {
    caption: isOn ? live : undefined,
    isShown,
    toggleShown: canShow ? toggleShown : undefined,
    error: isShown ? live.error : null,
    takeTranscript,
    recordingId: isOn ? live.recordingId : null,
  };
};
